import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Internal viewer — receives a fresh `initialIndex` on every open via the key
 * prop on the outer wrapper. State lives here so it auto-resets on re-mount.
 */
function LightboxView({ items, initialIndex, onClose, testId }) {
  const [idx, setIdx] = useState(initialIndex);
  const [uiVisible, setUiVisible] = useState(true);

  const prev = useCallback(
    () => setIdx((i) => (i - 1 + items.length) % items.length),
    [items.length],
  );
  const next = useCallback(
    () => setIdx((i) => (i + 1) % items.length),
    [items.length],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose]);

  const current = items[idx];
  if (!current) return null;

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-md flex items-center justify-center"
      onClick={() => setUiVisible((v) => !v)}
    >
      {/* Image with drag-to-swipe */}
      <motion.div
        key={current.url}
        className="relative max-w-[100vw] max-h-[100vh] flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={(_, info) => {
          if (info.offset.x < -60) next();
          else if (info.offset.x > 60) prev();
        }}
      >
        <img
          src={current.url}
          alt={current.caption || ""}
          className="max-w-[100vw] max-h-[100vh] object-contain select-none"
          draggable={false}
          data-testid={`${testId}-image`}
        />
      </motion.div>

      {/* UI overlay — toggleable */}
      <AnimatePresence>
        {uiVisible && (
          <motion.div
            key="lightbox-ui"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              data-testid={`${testId}-close`}
              aria-label="Fermer"
              className="pointer-events-auto absolute top-4 right-4 w-10 h-10 rounded-full inline-flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white transition-colors"
              style={{ marginTop: "env(safe-area-inset-top)" }}
            >
              <X className="w-4 h-4" />
            </button>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    prev();
                  }}
                  data-testid={`${testId}-prev`}
                  aria-label="Précédent"
                  className="pointer-events-auto hidden sm:inline-flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    next();
                  }}
                  data-testid={`${testId}-next`}
                  aria-label="Suivant"
                  className="pointer-events-auto hidden sm:inline-flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 text-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            <div
              className="pointer-events-none absolute bottom-0 inset-x-0 px-6 pb-6 pt-12 bg-gradient-to-t from-black/80 to-transparent"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
            >
              <div className="max-w-3xl mx-auto flex items-end justify-between gap-4">
                <p className="text-white/80 text-sm font-body line-clamp-2">
                  {current.caption || ""}
                </p>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-body shrink-0">
                  {idx + 1} / {items.length}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/**
 * ImmersiveLightbox — full-screen image gallery with swipe + tap-to-toggle-UI.
 *
 * Props:
 *  - items: Array<{ url: string, caption?: string }>
 *  - openIndex: number | null  — index to open, null = closed
 *  - onClose: () => void
 *  - testId?: string
 */
export default function ImmersiveLightbox({
  items = [],
  openIndex,
  onClose,
  testId = "immersive-lightbox",
}) {
  return (
    <AnimatePresence>
      {openIndex != null && (
        <LightboxView
          key={openIndex}
          items={items}
          initialIndex={openIndex}
          onClose={onClose}
          testId={testId}
        />
      )}
    </AnimatePresence>
  );
}
